import React from 'react';
import { useTranslation } from 'react-i18next';

const FeeSelectionModal = ({ isOpen, onClose, fees, selectedFeeId, onSelect }) => {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  const handleSelect = (feeId) => {
    onSelect(feeId);
    onClose();
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'delivery':
        return 'bg-blue-100 text-blue-700';
      case 'service':
        return 'bg-green-100 text-green-700';
      case 'packaging':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-6 h-6 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-white">{t('fees.selectFee')}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* No Fee Option */}
          <div
            onClick={() => handleSelect('')}
            className={`mb-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
              !selectedFeeId
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                  !selectedFeeId ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`}>
                  {!selectedFeeId && (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{t('fees.noFee')}</p>
                  <p className="text-sm text-gray-500">{t('fees.noFeeDescription')}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{t('common.currency')} 0.00</p>
              </div>
            </div>
          </div>

          {/* Fee Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fees.map((fee) => (
              <div
                key={fee.id}
                onClick={() => handleSelect(fee.id)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedFeeId === fee.id
                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start flex-1">
                    <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center flex-shrink-0 ${
                      selectedFeeId === fee.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                    }`}>
                      {selectedFeeId === fee.id && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-1">
                        {i18n.language === 'ar' && fee.name_ar ? fee.name_ar : fee.name}
                      </h3>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(fee.type)}`}>
                        {fee.type.charAt(0).toUpperCase() + fee.type.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-2xl font-bold text-blue-600">
                      {t('common.currency')} {parseFloat(fee.amount).toFixed(2)}
                    </p>
                  </div>
                </div>

                {fee.description && (
                  <p className="text-sm text-gray-600 mt-2 pl-9">
                    {fee.description}
                  </p>
                )}

                {/* Show both languages if available */}
                {fee.name_ar && i18n.language !== 'ar' && (
                  <p className="text-xs text-gray-500 mt-2 pl-9" dir="rtl">
                    {fee.name_ar}
                  </p>
                )}
              </div>
            ))}
          </div>

          {fees.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500">{t('fees.noFees')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeeSelectionModal;
